import { FC } from "react";
import { observer } from "mobx-react";
import { Box, IconButton } from "@mui/material";

import ChatMessage from "../../chat/components/ChatMessage";
import ChatInput from "../../chat/components/ChatInput";
import Column from "../../ui/core/Column";
import DropZoneWrapper from "../../ui/DropZoneWrapper";
import { CloseIcon } from "../../icons";
import { useClaimsStore } from "../../../hooks/useClaimsStore";
import { useChatStore } from "../../../hooks/useChatStore";
import { ChatHelper } from "../../helpers/chatHelper";
import { DEFAULT_FILE_ACCEPT } from "../../../config/channels";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";
import ClaimWelcomeContent from "./ClaimWelcomeContent";

interface ClaimPredefaultQuestionPreviewProps {
  hideInput?: boolean;
}

// Renders in place of the claims table (Claims page) once an AskReveActionMenu
// ActionRow is clicked, so the user can confirm/send the predefault question
// via the real ChatInput instead of it firing off immediately.
const ClaimPredefaultQuestionPreview: FC<ClaimPredefaultQuestionPreviewProps> =
  observer(({ hideInput }) => {
    const {
      claimActivePredefaultQuestion,
      setClaimActivePredefaultQuestion,
      predefaultQuestions,
    } = useClaimsStore();
    const chatStore = useChatStore();
    const { canUploadAttachments } = useCapabilities();

    if (!claimActivePredefaultQuestion) return null;

    const handleClose = () => {
      setClaimActivePredefaultQuestion(null);
    };

    // Placeholder copy carries its own bold emphasis via markdown, so this
    // ephemeral preview message doesn't need a separate styling prop chain.
    const systemMessage = ChatHelper.normalizeMessage("", {
      id: "predefault-question-preview-system",
      content: claimActivePredefaultQuestion.placeholder || "",
      sender: "other",
    });

    if (claimActivePredefaultQuestion.isWelcome) {
      return (
        <DropZoneWrapper
          className="h-full min-h-0 min-w-0 overflow-hidden flex flex-col items-stretch w-full"
          onFilesDropped={(files) => chatStore.setDroppedFiles(files as any)}
          acceptTypes={DEFAULT_FILE_ACCEPT}
          disabled={!canUploadAttachments}
        >
          <Column sx={{ height: "100%", minHeight: 0 }}>
            <ClaimWelcomeContent onClose={handleClose} />
            {hideInput ? null : (
              <ChatInput forceEnable preDefaultQuestions={predefaultQuestions} />
            )}
          </Column>
        </DropZoneWrapper>
      );
    }

    return (
      <DropZoneWrapper
        className="h-full min-h-0 min-w-0 overflow-hidden flex flex-col items-stretch w-full"
        onFilesDropped={(files) => chatStore.setDroppedFiles(files as any)}
        acceptTypes={DEFAULT_FILE_ACCEPT}
        disabled={!canUploadAttachments}
      >
        <Column>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <IconButton onClick={handleClose} size="small" aria-label="Close">
              <CloseIcon size={18} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Column
              sx={{
                width: "100%",
                minWidth: 0,
                marginTop: "auto",
                padding: "1rem",
                height: "unset",
              }}
            >
              <ChatMessage message={systemMessage} isCrossUserMessage />
            </Column>
          </Box>

          {hideInput ? null : (
            <ChatInput forceEnable preDefaultQuestions={predefaultQuestions} />
          )}
        </Column>
      </DropZoneWrapper>
    );
  });

export default ClaimPredefaultQuestionPreview;
